/**
 * Which parts of a schematic lie inside its external interfaces (the
 * consumable) and which lie outside them. A wash can pick out either side, so
 * one or the other stands out. The outside is not "the instrument": parts
 * beyond the interfaces may be single-use themselves, from another operation.
 * Presentation only; nothing reads it but the renderer.
 *
 * A line attached to an external port crosses the boundary, and the component
 * that owns that port is inside. So everything connected to such an owner
 * without crossing a boundary line is inside; what the boundary lines lead to,
 * and anything connected to nothing, is outside. Every external port counts, instrument
 * connections included, so pneumatic drives and the lines to them are on the
 * instrument side.
 *
 * Only an external port with a line on it marks its owner as inside.
 * One left open (a sensor's signal connection, a vent's atmosphere side, a gas
 * supply's far end) says the part faces the world, not which side of the
 * boundary it is on.
 *
 * A clamp-on part (`mounting: clamp_on`, D34) sits on a tube rather than in it,
 * so it is outside wherever it is drawn. The tube through it is inside, and the
 * inside stays connected through it.
 *
 * An instrument part (`mounting: instrument`, D66: its supplies, regulators
 * and solenoid valves) is outside wherever it is drawn, and so is everything
 * reached only through it: the inside never runs through the instrument, and
 * its own external ports (a supply's outlet) are not the set's interfaces.
 * Supplies and regulators are the instrument's by role, so one placed from a
 * template version that predates the marking is too. Where no interface is
 * marked, a line from an instrument part into another part is the crossing,
 * and the part it reaches is inside.
 *
 * With no line on any external port there is no boundary, and neither side is
 * washed.
 */
import type { DesignDocument, NodeDocument, TemplateDocument } from "../design/document";

export interface Partition {
  /** Nodes, edges and blocks inside the external interfaces: the consumable. */
  inside: ReadonlySet<string>;
  /** Nodes, edges and blocks outside them. */
  outside: ReadonlySet<string>;
}

export const NO_PARTITION: Partition = { inside: new Set(), outside: new Set() };

function owner(ref: string): string {
  return ref.split(".")[0]!;
}

function mountingOf(node: NodeDocument, template: TemplateDocument | undefined): unknown {
  return node.parameters?.mounting ?? template?.parameters?.find((parameter) => parameter.key === "mounting")?.default;
}

/** Whether a component is clamped on a tube, as the tubing checks read it. */
export function isClampOn(node: NodeDocument, template: TemplateDocument | undefined): boolean {
  return mountingOf(node, template) === "clamp_on";
}

/** Roles that are always the instrument's, whatever version placed them: a regulator placed before it said so included. */
const INSTRUMENT_ROLES: ReadonlySet<string> = new Set(["supply", "regulator"]);

/** Whether a component is the instrument's own, off the set: a supply, a regulator, a solenoid valve (D66). */
export function isInstrumentPart(node: NodeDocument, template: TemplateDocument | undefined): boolean {
  return INSTRUMENT_ROLES.has(node.role) || mountingOf(node, template) === "instrument";
}

export function partition(
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>> = {},
): Partition {
  const nodes = design.nodes ?? [];
  const edges = design.edges ?? [];
  const templateOf = (node: NodeDocument) => (node.template_version_id ? templates[node.template_version_id] : undefined);
  const clampOn = new Set(nodes.filter((node) => isClampOn(node, templateOf(node))).map((node) => node.local_id));
  const instrument = new Set(nodes.filter((node) => isInstrumentPart(node, templateOf(node))).map((node) => node.local_id));

  const externalPorts = new Set<string>();
  for (const node of nodes) {
    if (instrument.has(node.local_id)) continue;
    for (const port of node.ports ?? []) {
      if (port.is_external) externalPorts.add(`${node.local_id}.${port.local_id}`);
    }
  }
  const crossings = new Set<string>();
  for (const edge of edges) {
    for (const end of [edge.from_port, edge.to_port]) if (externalPorts.has(end)) crossings.add(owner(end));
  }
  // With no interface marked, a line from the instrument into another part is
  // where the set begins. Where interfaces are marked, they are the boundary,
  // and what lies between them and the instrument (a gauge, a junction on an
  // air manifold) is the instrument's side too.
  if (crossings.size === 0) {
    for (const edge of edges) {
      const [from, to] = [owner(edge.from_port), owner(edge.to_port)];
      if (instrument.has(from) !== instrument.has(to)) crossings.add(instrument.has(from) ? to : from);
    }
  }
  if (crossings.size === 0) return NO_PARTITION;

  // Connections that do not cross the boundary. Clamp-on parts carry the
  // connection, because the tube through them does.
  const neighbours = new Map<string, string[]>();
  const link = (a: string, b: string) => neighbours.set(a, [...(neighbours.get(a) ?? []), b]);
  for (const edge of edges) {
    if (externalPorts.has(edge.from_port) || externalPorts.has(edge.to_port)) continue;
    if (instrument.has(owner(edge.from_port)) || instrument.has(owner(edge.to_port))) continue;
    link(owner(edge.from_port), owner(edge.to_port));
    link(owner(edge.to_port), owner(edge.from_port));
  }

  const reached = new Set<string>();
  const queue = [...crossings];
  while (queue.length > 0) {
    const next = queue.pop()!;
    if (reached.has(next)) continue;
    reached.add(next);
    queue.push(...(neighbours.get(next) ?? []));
  }

  const inside = new Set<string>();
  const outside = new Set<string>();
  for (const node of nodes) {
    const ref = node.local_id;
    if (node.mount) continue;
    (reached.has(ref) && !clampOn.has(ref) ? inside : outside).add(ref);
  }
  // A line on an external port is the crossing itself, so it is outside. Any
  // other line is inside when both its ends are within reach (a clamp-on
  // part's tube included), and otherwise runs out beyond the interfaces.
  for (const edge of edges) {
    const crossing = externalPorts.has(edge.from_port) || externalPorts.has(edge.to_port);
    const within = reached.has(owner(edge.from_port)) && reached.has(owner(edge.to_port));
    (within && !crossing ? inside : outside).add(edge.local_id);
  }
  // A part mounted on a tube (D40) has no lines of its own: an instrument's
  // clamp-on part is outside, and the set's own (a manual clamp) is on the
  // side of the tube it sits on.
  for (const node of nodes) {
    if (!node.mount) continue;
    const onInside = inside.has(node.mount.edge) && !clampOn.has(node.local_id);
    (onInside ? inside : outside).add(node.local_id);
  }
  // A block is on a side when everything it holds is.
  for (const instance of design.group_instances ?? []) {
    const members = instance.member_local_ids ?? [];
    if (members.length === 0) continue;
    if (members.every((member) => inside.has(member))) inside.add(instance.local_id);
    else if (members.every((member) => outside.has(member))) outside.add(instance.local_id);
  }
  return { inside, outside };
}
